import React, { useCallback, useEffect, useMemo, useRef } from "react";
import * as S from "./HologramTour.styles";
import { absoluteAssetUrl, getOptimizedImageUrl } from "util/assets";
import { useVideoCleanup } from "hooks/useDestroyVideoRef";
import { useMedia } from "react-use";
import { BREAKPOINTS } from "@ubnt/store.core/lib/constants/layout";
import { IAnimationConfig } from "./HologramTour.types";

import rackMp4 from "./assets/rack.mp4";
import rackJpg from "./assets/rack.jpg";
import tabletBezelPng from "./assets/tablet-bezel.png";
import iphoneBezelPng from "./assets/iphone-bezel.png";

interface ITourContentProps {
  animation: IAnimationConfig;
  scale: number;
  onPlaybackEnd: () => void;
  onCanPlayVideo: () => void;
  isInView?: boolean;
}

export const TourContent = ({
  animation,
  scale,
  onPlaybackEnd,
  onCanPlayVideo,
  isInView,
}: Readonly<ITourContentProps>) => {
  const hologramVideoRef = useRef<HTMLVideoElement | null>(null);
  const rackRef = useRef<HTMLVideoElement | null>(null);
  const ipadVideoRef = useRef<HTMLVideoElement | null>(null);
  const iphoneVideoRef = useRef<HTMLVideoElement | null>(null);

  useVideoCleanup(hologramVideoRef);
  useVideoCleanup(ipadVideoRef);
  useVideoCleanup(iphoneVideoRef);
  useVideoCleanup(rackRef);

  const isTabletOrSmaller = useMedia(BREAKPOINTS.TABLET_AND_DOWN);

  const ipadVideoSrc = useMemo(() => {
    return isTabletOrSmaller
      ? animation.desktop.srcMobile
      : animation.desktop.src;
  }, [animation.desktop, isTabletOrSmaller]);

  const hologramVideoSrc = useMemo(() => {
    return isTabletOrSmaller
      ? animation.hologram.srcMobile
      : animation.hologram.src;
  }, [animation.hologram, isTabletOrSmaller]);

  const iphoneVideoSrc = useMemo(() => {
    return isTabletOrSmaller
      ? animation.mobile.srcMobile
      : animation.mobile.src;
  }, [animation.mobile, isTabletOrSmaller]);

  const playVideo = useCallback(async (videoRef) => {
    try {
      videoRef.current.currentTime = 0;
      await videoRef.current?.play();
    } catch (error) {
      console.error("Error during video playback:", error);
    }
  }, []);

  const pauseVideo = useCallback(async (videoRef) => {
    try {
      await videoRef.current?.pause();
    } catch (error) {
      console.error("Error during video pause:", error);
    }
  }, []);

  useEffect(() => {
    const refs = [hologramVideoRef, ipadVideoRef, iphoneVideoRef, rackRef];

    if (isInView) {
      refs.forEach(async (ref) => {
        await playVideo(ref);
      });
    }

    return () => {
      refs.forEach((ref) => {
        if (ref.current) {
          ref.current.pause();
        }
      });
    };
  }, [isInView, playVideo, pauseVideo]);

  return (
    <S.TourContainer $isVisible={true}>
      <S.HologramContainer>
        <S.HologramVideo
          ref={hologramVideoRef}
          src={hologramVideoSrc}
          poster={getOptimizedImageUrl(
            absoluteAssetUrl(animation.hologram.poster),
            1138,
          )}
          muted
          autoPlay
          playsInline
          onEnded={onPlaybackEnd}
        />
      </S.HologramContainer>

      <S.RackVideo
        ref={rackRef}
        src={rackMp4}
        loop
        muted
        playsInline
        autoPlay
        preload="metadata"
        poster={getOptimizedImageUrl(absoluteAssetUrl(rackJpg), 924)}
      />

      <S.BezelContainer>
        <S.BezelImageContainer>
          <S.BezelImage
            loading="eager"
            alt="tablet bezel"
            src={getOptimizedImageUrl(absoluteAssetUrl(tabletBezelPng), 1122)}
            srcSet={`${getOptimizedImageUrl(
              absoluteAssetUrl(tabletBezelPng),
              2244,
            )} 2x`}
          />
        </S.BezelImageContainer>

        <S.VideoWrapper>
          <S.ScreenVideo
            ref={ipadVideoRef}
            muted
            playsInline
            preload="auto"
            autoPlay
            src={ipadVideoSrc}
            poster={getOptimizedImageUrl(
              absoluteAssetUrl(animation.desktop.poster),
              2244,
            )}
            onCanPlay={onCanPlayVideo}
          />
        </S.VideoWrapper>
      </S.BezelContainer>

      <S.MobileAbsoluteContainer>
        <S.MobileBezelContainer $scale={scale}>
          <S.IphoneBezelContainer>
            <S.IphoneBezelImage
              alt="iphone"
              loading="eager"
              src={getOptimizedImageUrl(absoluteAssetUrl(iphoneBezelPng), 224)}
              srcSet={`${getOptimizedImageUrl(
                absoluteAssetUrl(iphoneBezelPng),
                448,
              )} 2x`}
            />
          </S.IphoneBezelContainer>

          <S.IphoneVideoWrapper>
            <S.IphoneScreenVideo
              ref={iphoneVideoRef}
              muted
              playsInline
              autoPlay
              preload="metadata"
              src={iphoneVideoSrc}
              poster={getOptimizedImageUrl(
                absoluteAssetUrl(animation.mobile.poster),
                548,
              )}
            />
          </S.IphoneVideoWrapper>
        </S.MobileBezelContainer>
      </S.MobileAbsoluteContainer>
    </S.TourContainer>
  );
};
